'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, Activity, Coins, MessageSquare, Box } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BoardCard } from './BoardCard';
import { BoardroomCard } from './BoardroomCard';
import { isBoardNew, isFloorPending, type MockBoard } from '../data/mock-boards';
import { activityScore } from '../data/activity-score';
import { useLobbyBoards } from '@/hooks/use-lobby-boards';
import { useLobbySeatHoldings } from '@/hooks/use-lobby-seat-holdings';
import { useBoardsChatUnread } from '@/hooks/use-boards-chat-unread';
import { markRoomsSeen } from '@/lib/chat-mark-seen';
import { useWallet } from '@/hooks/use-wallet';
import { useNowSec } from '@/hooks/use-now-sec';
import {
  BOARDROOM_BOARD_ID,
  getBoardroomLiveGridId,
} from '../data/boardroom-grid';
import { selectMyBoards } from './BoardDirectory.helpers';

// Sort vocabulary:
//   - `'activity'` (default) — composite score combining holders +
//     last-buy recency + cumulative tax + 7d chat traffic. Designed
//     to surface "alive *right now*" boards regardless of whether
//     they generate yield or just chat tax. See `activity-score.ts`
//     for the formula and weight rationale.
//   - `'taxes'` — cumulative Harberger tax (`MockBoard.taxesUsd`),
//     for viewers who want raw economic signal.
//   - `'chat'` — pure 7d chat-message count, for "where is the
//     conversation happening?". Reads `chatMessages7d` aggregated
//     server-side from `chat_messages` (`getMessageCountsByRoomSince`).
//   - `'floor'` — seat floor price; "which boards are expensive?".
//
// The previous `'rewards'` sort was removed: the 7d-rewards window
// is biased toward fresh distributions and missed long-tail
// activity, and the activity composite captures rewards' signal
// (recent buys correlate with fresh distributions) without
// punishing chat-only successes.
type SortKey = 'activity' | 'taxes' | 'chat' | 'floor';
// Curated stays disabled per the user's request — the data layer
// (curated bucketing in `useLobbyBoards`, the curated-only NEW-board
// sorter below) keeps running so flipping it back on is a literal
// uncomment of the `'curated'` member, the `TAB_LABELS.curated` row,
// and the `tab === 'curated' ? curated : …` branch in `activeList`.
type TabKey = 'community' | 'myBoards';
// type TabKey = 'curated' | 'community' | 'myBoards';

const SORT_LABELS: Record<SortKey, string> = {
  activity: 'Activity',
  taxes: 'Taxes',
  chat: 'Chat',
  floor: 'Floor',
};
const SORT_ICONS: Record<SortKey, React.ReactNode> = {
  activity: <Activity className="h-3 w-3" />,
  taxes: <Coins className="h-3 w-3" />,
  chat: <MessageSquare className="h-3 w-3" />,
  floor: (
    <span
      className="inline-flex text-amber-400/95"
      style={{
        transform: 'perspective(18px) rotateX(52deg) rotateZ(-8deg)',
        transformStyle: 'preserve-3d' as const,
      }}
      aria-hidden
    >
      <Box className="h-3 w-3" strokeWidth={2.25} />
    </span>
  ),
};

const TAB_LABELS: Record<TabKey, string> = {
  community: 'Community',
  myBoards: 'My Boards',
  // curated: 'Curated',
};

interface BoardDirectoryProps {
  activeId?: string;
  /**
   * Board id that the user has just tapped but whose route hasn't
   * landed yet. The matching card paints a darkened spinner overlay
   * so a slow nav doesn't read as an unresponsive tap. Plumbed from
   * `LobbyClient.pendingBoardId`.
   */
  pendingId?: string | null;
  onSelect: (id: string) => void;
  className?: string;
  /** When true (the mobile sheet), the board list stretches the full height
   *  available from the parent container. Desktop caps via its own
   *  `GamePanel` chrome. */
  fillHeight?: boolean;
  /** Suppress the My Boards unread cues (tab-pill dot, per-row pip, and
   *  the aggregate "Mark all as read" action) on mobile surfaces.
   *
   *  Mobile already surfaces the identical "any of my boards has unread
   *  chat" signal on the bottom-nav Chat tab (see `AppBottomNav`), so
   *  painting the same dot on the My Boards tab here would double up the
   *  cue and risk looking stale after the viewer opens chat. Desktop
   *  has no bottom nav and relies on the in-panel dots, so it leaves
   *  this at the default (`false`). */
  suppressMyBoardsUnread?: boolean;
  /**
   *  Drop the My Boards tab entirely. Mobile already surfaces the
   *  viewer's owned boards via the always-visible top carousel
   *  (`MobileBoardRail`), so the duplicated tab adds noise in a tight
   *  layout. Desktop keeps the tab (no equivalent rail). When the
   *  prop is true the tab pill is removed and `community` is forced
   *  as the active list — the My Boards data still computes for
   *  `MobileBoardRail`'s consumer hook independently.
   */
  hideMyBoardsTab?: boolean;
}

/**
 * Search + sort + board list with two tabs:
 *
 *   - **Community** — every live grid the gateway exposes that didn't
 *     get bucketed into `pinned` or `curated` upstream. This is the
 *     default tab and matches the long-standing single-list behavior
 *     before tabs returned.
 *   - **My Boards** — boards the connected viewer holds at least one
 *     tile on, derived from `useLobbySeatHoldings`. Each row also
 *     carries a red-dot pip when the board's chat has new messages
 *     since the viewer last visited that board (see
 *     `useBoardsChatUnread` for the per-room last-seen tracking).
 *
 * Curated stays disabled — the bucket is still computed in
 * `useLobbyBoards` so re-enabling the rail later is a small uncomment
 * (`TabKey`, `TAB_LABELS`, `activeList` branch). The NEW pill in
 * `BoardCard` and the curated freshness sort below stay live for the
 * same reason.
 *
 * Shared between the desktop `BoardListPanel` (left column) and the
 * mobile full-bleed lobby view (rendered by `LobbyClient` when
 * `?mode=lobby` is set). Sort is whichever stat the viewer has
 * selected (favorites previously pinned to the top of every list
 * but the affordance is currently disabled — see `BoardCard`).
 */
export function BoardDirectory({
  activeId,
  pendingId,
  onSelect,
  className,
  fillHeight,
  suppressMyBoardsUnread = false,
  hideMyBoardsTab = false,
}: BoardDirectoryProps) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('activity');
  const [sortOpen, setSortOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('community');

  const { isConnected: viewerConnected, address: viewerAddress } = useWallet();
  // Lowercased once per render so the My Boards `creatorAddress`
  // match (added below) doesn't re-case on every comparison. Boards
  // already store `creatorAddress` lowercased — see
  // `gridSummaryToMockBoard`.
  const viewerLower = viewerAddress ? viewerAddress.toLowerCase() : null;
  const { pinned, community: liveCommunity, curated: liveCurated, isLoading } =
    useLobbyBoards();

  // Holdings drive the My Boards tab: every group is a board where
  // the viewer holds at least one (active or zombie) tile. The hook
  // returns boardIds as the live composite (`<ugm>-<tokenId>`) — for
  // the Boardroom we map that back to the `'boardroom'` lobby alias
  // so the same id contract `MockBoard.id` uses works for both lookup
  // and chat-room id.
  const { holdings, isLoading: holdingsLoading } = useLobbySeatHoldings();

  // Same 60s tick `useLobbyBoards` uses for its curated/community
  // bucketing (and `BoardCard` uses for its NEW pill). Re-deriving
  // it here so the curated-tab "freshness pin" sort, the activity
  // composite's recency-decay, and the community alive/dormant
  // filter all share a single per-render clock — without that, two
  // boards near the same recency boundary could be scored with
  // slightly different "now"s in the same comparator pass.
  const nowSec = useNowSec(60_000);

  const { boardroom, community, curated, myBoards, viewerHoldsBoardroom } = useMemo(() => {
    const br = pinned[0] ?? null;

    const filterList = (list: MockBoard[]) =>
      q
        ? list.filter(
            (b) =>
              (b.name ?? '').toLowerCase().includes(q.toLowerCase()) ||
              (b.tagline ?? '').toLowerCase().includes(q.toLowerCase()),
          )
        : list;

    // Each comparator returns descending order ("more" wins) — the
    // activity composite is a unit-less score so we never display
    // its raw value; `taxesUsd` and `floor` are pre-formatted USD
    // strings parsed back through `parseNum`; chat is a raw integer.
    //
    // Favorites previously pinned to the top of every list, but the
    // favoriting affordance has been removed from `BoardCard` (and
    // the `useFavorites` read from `MobileBoardRail`) pending a
    // re-wire — without a way to add favorites the tier would never
    // fire, so it's dropped from the comparator entirely.
    const sorter = (a: MockBoard, b: MockBoard) => {
      switch (sort) {
        case 'floor':
          return parseNum(b.floor) - parseNum(a.floor);
        case 'taxes':
          return parseNum(b.taxesUsd) - parseNum(a.taxesUsd);
        case 'chat':
          return b.chatMessages7d - a.chatMessages7d;
        case 'activity':
        default:
          return activityScore(b, nowSec) - activityScore(a, nowSec);
      }
    };

    // Within the curated rail specifically, push freshly-launched
    // boards (< 4h since `createdAtSec`) above everything else.
    // Without this tier they'd sort to the bottom of a stat-
    // descending list — a brand-new board has had no time to accrue
    // tax / chat / yield, so its scores all collapse to ~0 and the
    // default sorter buries it under every established board on the
    // rail. That makes the NEW pill we just added invisible and
    // defeats the point of promoting fresh boards into curated in
    // the first place.
    //
    // (The favorites tier that previously sat above this one was
    // removed alongside the favorite-star UI — see `sorter` above.)
    //
    // Activity sort partially handles this on its own — a brand-new
    // board with even one buy gets a recency multiplier of ~1.0 —
    // but the curated rail's freshness pin is stricter ("< 4h
    // regardless of activity") so we keep the explicit tier here.
    //
    // Community keeps the plain sorter — a NEW board appears in
    // community only when it has *some* signal (the activity filter
    // below already gates it), so it doesn't need the same boost.
    const curatedSorter = (a: MockBoard, b: MockBoard) => {
      const newDiff = (isBoardNew(b, nowSec) ? 1 : 0) - (isBoardNew(a, nowSec) ? 1 : 0);
      if (newDiff !== 0) return newDiff;
      switch (sort) {
        case 'floor':
          return parseNum(b.floor) - parseNum(a.floor);
        case 'taxes':
          return parseNum(b.taxesUsd) - parseNum(a.taxesUsd);
        case 'chat':
          return b.chatMessages7d - a.chatMessages7d;
        case 'activity':
        default:
          return activityScore(b, nowSec) - activityScore(a, nowSec);
      }
    };

    // While the curated tab is hidden (see the TabKey block above),
    // route the curated bucket through the Community tab so freshly-
    // launched boards stay visible. `useLobbyBoards` puts every
    // `isBoardNew` grid into `liveCurated`; without folding it back in
    // here a creator's just-launched board would land nowhere a user
    // can browse to. When curated returns, drop this union and let
    // each tab consume its own bucket.
    const communitySource = [...liveCurated, ...liveCommunity];

    // Default Community view hides truly dormant boards so the lobby
    // doesn't drown in empty grids. A board is "alive enough to show"
    // when *any* of these are true:
    //
    //   - it has at least one holder (chat-tax-only success — players
    //     paying tax just to be in chat, no yield flowing);
    //   - it has collected non-zero Harberger tax (cumulative
    //     economic signal);
    //   - it has had any chat traffic in the last 7d (off-chain
    //     social signal);
    //   - it's brand-new (within `isBoardNew`'s 4h window) so a fresh
    //     launch is visible immediately.
    //
    // The OR-logic across all four prevents the "rewards-only" trap
    // the prior filter had — yield-less boards with active holders
    // and chat used to vanish from the default lobby. Search box
    // bypasses the filter entirely so any board remains reachable
    // by name/tagline regardless of its activity state.
    const isBoardAlive = (b: MockBoard): boolean =>
      b.uniqueHolderCount > 0 ||
      b.taxesUsd !== '$0' ||
      b.chatMessages7d > 0 ||
      isBoardNew(b, nowSec);

    const communityBase = q
      ? communitySource
      : communitySource.filter(isBoardAlive);

    // Translate holdings boardIds into the lobby's `MockBoard.id`
    // namespace. Live grids use the same composite (`<ugm>-<tokenId>`)
    // for both, but the Boardroom is keyed as the literal alias
    // `'boardroom'` everywhere in the lobby — see
    // `useLobbyBoards.brandBoardroomOverlay` for why. Without the
    // remap, holding a Boardroom seat would never match the pinned
    // card's id and the My Boards list would silently drop it.
    const boardroomLiveId = getBoardroomLiveGridId();
    const heldIds = new Set<string>();
    for (const h of holdings) {
      const id = h.boardId.toLowerCase();
      if (boardroomLiveId && id === boardroomLiveId.toLowerCase()) {
        heldIds.add(BOARDROOM_BOARD_ID);
      } else {
        heldIds.add(id);
      }
    }

    // Pull from every live bucket (pinned + curated + community)
    // unfiltered by the `$0` taxes rule — a viewer can hold tiles
    // on a dormant board and still want to see it here. Search box
    // applies on top so typing narrows the list the same way it does
    // in Community.
    //
    // The Boardroom appears here too when the viewer holds Boardroom
    // seats — it should read as "any other board you own" in this
    // tab. To avoid rendering twice (once as the pinned flagship card
    // at the top of the panel, once inline as a regular row), the
    // pinned `BoardroomCard` is suppressed below specifically on the
    // My Boards tab when the viewer is a Boardroom seat-holder. Other
    // tabs keep the pinned card so newcomers always see the flagship.
    //
    // A board also surfaces here when the viewer is its creator,
    // independent of whether they hold any seats on it — without
    // this branch a creator who hasn't bought back any of their own
    // primary-mint inventory would never see the board they just
    // launched in their own portfolio rail (the holdings filter
    // strips primary-mint creator seats upstream — see
    // `useLobbySeatHoldings:212` — so `heldIds` would be empty for a
    // brand-new creator). The two-rule predicate lives in
    // `selectMyBoards` so it can be unit-tested without booting the
    // whole panel.
    const allLive = [...pinned, ...liveCurated, ...liveCommunity];
    const myBoardsBase = selectMyBoards(allLive, heldIds, viewerLower);

    return {
      boardroom: br,
      community: [...filterList(communityBase)].sort(sorter),
      // `curated` is still computed even though the tab is hidden —
      // keeping the derivation live means re-enabling the rail is a
      // one-line addition to the `activeList` ternary rather than a
      // memo shape change.
      curated: [...filterList(liveCurated)].sort(curatedSorter),
      myBoards: [...filterList(myBoardsBase)].sort(sorter),
      viewerHoldsBoardroom: heldIds.has(BOARDROOM_BOARD_ID),
    };
  }, [pinned, liveCommunity, liveCurated, q, sort, nowSec, holdings, viewerLower]);

  // Per-room chat unread tracking for the My Boards red-dot. We feed
  // the hook every held room (regardless of which tab is active) so
  // when the user flips to My Boards the dots are already populated
  // — otherwise they'd flicker on once the deferred fetch resolves.
  // `LobbyClient` wires `markChatRoomSeen` on board-switch so visiting
  // a held board clears its dot on the next render.
  const myBoardRoomIds = useMemo(() => myBoards.map((b) => b.id), [myBoards]);
  const { hasUnread, latestIds } = useBoardsChatUnread(myBoardRoomIds);
  // Aggregate the per-row signal up to the tab itself: if any of the
  // viewer's owned boards has unread chat, the "My Boards" tab pill
  // gets the same red dot the rows do. Lets users notice new chat from
  // the Community tab without having to flip over and inspect rows.
  const myBoardsHasUnread = useMemo(
    () => myBoardRoomIds.some((id) => hasUnread(id)),
    [myBoardRoomIds, hasUnread],
  );

  // One-shot "Mark as read" — clears every red dot in the My Boards
  // list at once. Limited to rooms in the current My Boards set so it
  // never touches rooms the viewer hasn't opted into (Boardroom-as-
  // global, Community boards). The chat-sheet header bell tracks its
  // own global last-seen id keyed without a `:room` suffix and is
  // intentionally NOT cleared here.
  const handleMarkAllRead = useCallback(() => {
    if (!viewerAddress) return;
    markRoomsSeen(myBoardRoomIds, viewerAddress, latestIds);
  }, [viewerAddress, myBoardRoomIds, latestIds]);

  // When the My Boards tab is hidden (mobile), force the active tab
  // back to `community` if a parent flips the prop while the user
  // happens to sit on `myBoards`. Without this the activeList branch
  // would keep painting an empty My Boards list with no pill to switch
  // back to community.
  useEffect(() => {
    if (hideMyBoardsTab && tab === 'myBoards') {
      setTab('community');
    }
  }, [hideMyBoardsTab, tab]);

  const activeList =
    tab === 'myBoards' ? myBoards : community;
  // const activeList = tab === 'curated' ? curated : tab === 'myBoards' ? myBoards : community;

  return (
    <div className={cn('flex flex-col gap-2', fillHeight && 'h-full min-h-0', className)}>
      <div className="flex gap-1.5">
        <label className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search boards…"
            className="w-full h-8 pl-7 pr-2 text-[12px] rounded-[3px] bg-black/40 border border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-400/60 focus:shadow-[0_0_0_1px_rgba(255,215,120,0.25)]"
          />
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setSortOpen((v) => !v)}
            className="h-8 px-2 flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] rounded-[3px] bg-black/40 border border-amber-500/20 text-zinc-300 hover:border-amber-400/50 hover:text-amber-100 transition-colors"
          >
            {SORT_ICONS[sort]}
            <span>{SORT_LABELS[sort]}</span>
            <ChevronDown className={cn('h-3 w-3 transition-transform', sortOpen && 'rotate-180')} />
          </button>
          {sortOpen && (
            <div className="absolute right-0 top-[calc(100%+4px)] z-30 w-36 py-0.5 rounded-[3px] bg-[#0b0a08]/95 backdrop-blur-md border border-amber-500/30 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.9)]">
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setSort(k);
                    setSortOpen(false);
                  }}
                  className={cn(
                    'w-full px-2 py-1 text-left text-[10px] font-mono uppercase tracking-[0.12em] flex items-center gap-1.5',
                    sort === k
                      ? 'text-amber-100 bg-amber-500/10'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
                  )}
                >
                  {SORT_ICONS[k]}
                  {SORT_LABELS[k]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* The pinned Boardroom card stays visible on Community so the
          flagship is always one click away. On My Boards we suppress
          it for Boardroom seat-holders only — they see it inline as a
          regular row in their list (see `myBoardsBase` above), so the
          pinned card would be a duplicate. Non-holders on My Boards
          still see the pinned card so they can navigate to the
          flagship without leaving the tab. */}
      {boardroom && !(tab === 'myBoards' && viewerHoldsBoardroom) && (
        <div className="relative">
          <div className="flex items-center gap-1.5 px-1 mb-1">
            <span className="text-[8px] tracking-[0.25em] font-mono uppercase text-red-400/70">Pinned</span>
            <span className="flex-1 h-px bg-gradient-to-r from-red-500/40 to-transparent" />
          </div>
          <BoardroomCard
            board={boardroom}
            active={activeId === boardroom.id}
            pending={pendingId === boardroom.id}
            onSelect={() => onSelect(boardroom.id)}
          />
        </div>
      )}

      {/*
        Curated tab stays disabled — the bucket is still computed in
        `useLobbyBoards` so re-enabling the rail later is a small
        uncomment of the `'curated'` member in `TabKey` /
        `TAB_LABELS` and the `tab === 'curated' ? curated : …` branch
        in `activeList`. The NEW pill on `BoardCard` and the
        `curatedSorter` above keep running so nothing else in the
        lobby has to move when curated comes back.
      */}
      {/* Mobile drops the My Boards tab — the always-visible
          `MobileBoardRail` at the top of the lobby already covers the
          same dataset, so the pill would be redundant. With only
          Community remaining, the tablist itself disappears too so the
          panel doesn't render a single dead pill. */}
      {(Object.keys(TAB_LABELS) as TabKey[]).filter((k) => !(hideMyBoardsTab && k === 'myBoards')).length > 1 && (
      <div
        role="tablist"
        aria-label="Board lists"
        className="flex gap-0.5 p-0.5 rounded-[3px] bg-black/40 border border-amber-500/15"
      >
        {(Object.keys(TAB_LABELS) as TabKey[]).filter((k) => !(hideMyBoardsTab && k === 'myBoards')).map((k) => {
          const isActive = tab === k;
          // Surface the My Boards aggregate unread cue on the tab pill
          // itself so a viewer sitting on Community still sees that
          // their owned boards have new chat. The dot uses the same
          // `bg-brand-red` token as the per-row pip on `BoardCard` to
          // keep the language consistent across the surface.
          //
          // Suppressed on mobile (`suppressMyBoardsUnread`) because the
          // bottom-nav Chat tab already carries the identical aggregate
          // signal — painting both would double-count the same
          // notification.
          const showTabDot =
            !suppressMyBoardsUnread && k === 'myBoards' && myBoardsHasUnread;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(k)}
              className={cn(
                'relative flex-1 h-7 text-[10px] font-mono uppercase tracking-[0.15em] rounded-[2px] transition-colors',
                isActive
                  ? 'bg-amber-500/15 text-amber-100 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.25)]'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5',
              )}
            >
              <span className="relative inline-flex items-center justify-center">
                {TAB_LABELS[k]}
                {showTabDot && (
                  <span
                    aria-label="Unread chat in your boards"
                    className="absolute -top-0.5 -right-2 h-1.5 w-1.5 rounded-full bg-brand-red shadow-[0_0_4px_rgba(239,68,68,0.6)]"
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
      )}

      {tab === 'myBoards' && myBoardsHasUnread && !suppressMyBoardsUnread && (
        <div className="flex items-center justify-end -mt-0.5">
          <button
            type="button"
            onClick={handleMarkAllRead}
            className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-400 hover:text-amber-100 transition-colors"
          >
            Mark all as read
          </button>
        </div>
      )}

      <div
        data-gild-scroll
        className={cn(
          'flex flex-col gap-1 pr-1 -mr-1',
          fillHeight ? 'flex-1 min-h-0 overflow-y-auto' : 'overflow-y-auto',
        )}
      >
        {activeList.map((b) => (
          <BoardCard
            key={b.id}
            board={b}
            active={activeId === b.id}
            pending={pendingId === b.id}
            onSelect={() => onSelect(b.id)}
            // Red dot is My Boards-only by design — lighting it on
            // Community / pinned would surface "you have unread
            // chat" on every board with recent activity, which is
            // marketing pressure rather than signal. The chat-sheet
            // bell still covers the global "anyone said something
            // anywhere" case. Mobile also suppresses the per-row pip
            // because the bottom-nav Chat tab already aggregates the
            // same signal for the whole surface (see
            // `suppressMyBoardsUnread` prop docs).
            showUnread={
              !suppressMyBoardsUnread &&
              tab === 'myBoards' &&
              hasUnread(b.id)
            }
          />
        ))}
        {activeList.length === 0 && (
          <div className="text-center py-6 px-3 space-y-1.5">
            <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-[0.15em]">
              {emptyStateCopy({
                tab,
                q,
                isLoading,
                viewerConnected,
                holdingsLoading,
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function emptyStateCopy(args: {
  tab: TabKey;
  q: string;
  isLoading: boolean;
  viewerConnected: boolean;
  holdingsLoading: boolean;
}): string {
  const { tab, q, isLoading, viewerConnected, holdingsLoading } = args;
  if (q) return 'No boards match';
  if (tab === 'myBoards') {
    if (!viewerConnected) return 'Connect your wallet to see boards you own';
    if (holdingsLoading) return 'Loading your boards…';
    return "You don't hold any tiles yet";
  }
  // Community (and any future tab that surfaces live boards).
  if (isLoading) return 'Loading boards…';
  return 'No live boards yet';
}

function parseNum(s: string): number {
  // Pending USD sentinel sorts to the bottom of the Floor sort the same
  // way `$0` does today — the price oracle hasn't resolved yet so we
  // can't rank these against fully-priced boards. Explicit guard keeps
  // this contract obvious even if the sentinel string later gains
  // characters that the digit regex below could partially match.
  if (isFloorPending(s)) return 0;
  const cleaned = s.replace(/^\$/, '').trim();
  const m = cleaned.match(/([\d,.]+)([km]?)/i);
  if (!m) return 0;
  const n = parseFloat(m[1].replace(/,/g, ''));
  const mul = m[2]?.toLowerCase() === 'k' ? 1000 : m[2]?.toLowerCase() === 'm' ? 1_000_000 : 1;
  return n * mul;
}
